import {
	type BdcServiceConfig,
	ProductConfiguration,
	type ProgramOwnerServiceConfig,
	Services,
} from '@ekhodealer/ekho-common';

import { useCurrentServicesProvider } from './useCurrentServicesProvider';
import { useGetUserServicesProviders } from './useGetUserServicesProviders';

interface ShouldHideSubscribeCtaResult {
	shouldHide: boolean;
	isLoading: boolean;
}

/**
 * Determines whether the "Get Started" subscribe CTA should be hidden
 * for the current services provider. Certain program owners cannot
 * self-serve subscribe and must go through sales:
 *   - Access program owners
 *   - Omni program owners
 *   - Dealer group program owners with >1 SP in their network
 */
export function useShouldHideSubscribeCta(): ShouldHideSubscribeCtaResult {
	const { data: provider, isLoading: isProviderLoading } = useCurrentServicesProvider();
	const { servicesProviders, isLoading: isSpsLoading } = useGetUserServicesProviders();

	const isLoading = isProviderLoading || isSpsLoading;

	if (isLoading || !provider) {
		return { shouldHide: false, isLoading };
	}

	const bdcConfig = provider.config?.[Services.BDC] as BdcServiceConfig | undefined;
	if (bdcConfig?.salesEnabled) {
		return { shouldHide: false, isLoading: false };
	}

	const programOwnerConfig = provider.config?.[Services.PROGRAM_OWNER] as
		| ProgramOwnerServiceConfig
		| undefined;

	if (!programOwnerConfig?.supported) {
		return { shouldHide: false, isLoading: false };
	}

	const product = programOwnerConfig.product;

	if (product === ProductConfiguration.ACCESS || product === ProductConfiguration.OMNI) {
		return { shouldHide: true, isLoading: false };
	}

	if (product === ProductConfiguration.DEALER) {
		const networkSps = servicesProviders.filter((sp) =>
			sp.programOwnerIds?.includes(provider.id),
		);
		if (networkSps.length > 1) {
			return { shouldHide: true, isLoading: false };
		}
	}

	return { shouldHide: false, isLoading: false };
}
